/** @types node */
/// <reference types="node" />

/**
 * Logger padronizado — isomórfico (browser + Node).
 *
 * Suporta níveis: debug, info, success, warn, error.
 * Controlado por APP_LOG_LEVEL (Node) ou VITE_APP_LOG_LEVEL (browser).
 */

const LEVELS = { debug: 0, info: 1, success: 1, warn: 2, error: 3 } as const;

type LogLevel = keyof typeof LEVELS;

const isBrowser = typeof window !== 'undefined';

function getEnv(key: string): string | undefined {
  // Node
  if (typeof process !== 'undefined' && process.env?.[key]) {
    return process.env[key];
  }
  // Browser (Vite)
  if (typeof import.meta !== 'undefined' && import.meta.env?.[key]) {
    return import.meta.env[key];
  }
  return undefined;
}

function getGlobalLevel(): LogLevel {
  const level = getEnv('APP_LOG_LEVEL') || getEnv('VITE_APP_LOG_LEVEL');
  if (level && level in LEVELS) return level as LogLevel;
  return 'info';
}

export class Logger {
  private level: number;

  constructor(level: LogLevel = getGlobalLevel()) {
    this.level = LEVELS[level] ?? LEVELS.info;
  }

  private _log(level: LogLevel, prefix: string, message: string) {
    if (LEVELS[level] < this.level) return;

    if (isBrowser) {
      const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
      fn(`[${prefix}] ${message}`);
      return;
    }

    // Node — cores ANSI + timestamp
    const noColor = getEnv('NO_COLOR');
    const colors: Record<string, string> = {
      reset: '\x1b[0m',
      dim: '\x1b[2m',
      blue: '\x1b[34m',
      green: '\x1b[32m',
      yellow: '\x1b[33m',
      red: '\x1b[31m',
      cyan: '\x1b[36m',
    };
    const c = (text: string, color: string) =>
      noColor ? text : `${colors[color] || ''}${text}${colors.reset}`;
    const ts = c(new Date().toISOString().split('T')[1].split('.')[0], 'dim');
    const colorMap: Record<LogLevel, string> = {
      debug: 'dim',
      info: 'blue',
      success: 'green',
      warn: 'yellow',
      error: 'red',
    };
    // eslint-disable-next-line no-console
    console.log(`${ts} ${c(prefix, colorMap[level])} ${message}`);
  }

  debug(msg: string) {
    this._log('debug', 'DEBUG', msg);
  }

  info(msg: string) {
    this._log('info', 'INFO ', msg);
  }

  success(msg: string) {
    this._log('success', ' OK  ', msg);
  }

  warn(msg: string) {
    this._log('warn', 'WARN ', msg);
  }

  error(msg: string) {
    this._log('error', 'ERROR', msg);
  }

  /**
   * Logger que não imprime nada (útil para testes).
   */
  static silent(): Logger {
    return new Logger('error');
  }
}

export const defaultLogger = new Logger();
